import { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  fetchClinicPractitionerByEmail,
  clearPractitioner,
} from "../../../../redux/reducers/ClinicPractitioners.reducer";
import { fetchGHLStaffWithCalendars } from "../../../../redux/reducers/ghlStaffPractitioners.reducer";
import { setQuestionnaireStepper } from "../../../../redux/reducers/patientData.reducer";
import { BASE_URL } from "../../../../constants/config";
import { extractPractitionerData, buildGhlCalendarUrl } from "../utils";

const EMPTY_CLINIC_STATE = { data: null, loading: false, error: null };

export function usePractitionerDetail() {
  const { name } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const decodedName = decodeURIComponent(name).replace(/-/g, " ");

  const { data: staffPractitioners, loading: staffLoading } = useSelector(
    (state) => state.ghlStaffPractitioners,
  );
  const patientDataFromStore = useSelector((state) => state.patientData?.data);
  const clinicPractitionersState = useSelector(
    (state) => state.clinicPractitioner ?? EMPTY_CLINIC_STATE,
  );

  const practitionerFromStore = useMemo(() => {
    if (!staffPractitioners || staffPractitioners.length === 0) return null;
    return staffPractitioners.find(
      (p) => p.name?.toLowerCase() === decodedName.toLowerCase(),
    );
  }, [staffPractitioners, decodedName]);

  const userFromState = location.state?.user || practitionerFromStore;
  const patientData = location.state?.patientData || patientDataFromStore;
  const {
    data: clinicPractitioner = null,
    loading: reduxLoading = false,
    error: reduxError = null,
  } = clinicPractitionersState;

  useEffect(() => {
    if (!location.state?.user && staffPractitioners.length === 0) {
      dispatch(fetchGHLStaffWithCalendars());
    }
  }, [dispatch, location.state?.user, staffPractitioners.length]);

  const [assigningContact, setAssigningContact] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [error, setError] = useState(null);
  const [calendarWindowRef, setCalendarWindowRef] = useState(null);

  const assignmentCompletedRef = useRef(false);
  const patientDataRef = useRef(patientData);
  const windowCheckIntervalRef = useRef(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [savedCardInfo, setSavedCardInfo] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const paymentVerifiedRef = useRef(false);

  useEffect(() => {
    const checkPaymentVerification = async () => {
      if (!patientData?.email) return;
      setCheckingPayment(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/payments/check-verified-card`,
          {
            params: { email: patientData.email },
          },
        );
        const verified = response.data?.success && response.data?.verified;
        paymentVerifiedRef.current = !!verified;
        setCheckingPayment(false);
        setPaymentVerified(!!verified);
        setSavedCardInfo(verified ? response.data.card : null);
      } catch (err) {
        paymentVerifiedRef.current = false;
        setCheckingPayment(false);
        setPaymentVerified(false);
        setSavedCardInfo(null);
      }
    };
    checkPaymentVerification();
  }, [patientData?.email]);

  useEffect(() => {
    const practitionerEmail = userFromState?.email;
    if (practitionerEmail) {
      dispatch(fetchClinicPractitionerByEmail(practitionerEmail));
    }
    return () => dispatch(clearPractitioner());
  }, [dispatch, userFromState?.email]);

  useEffect(() => {
    assignmentCompletedRef.current = false;
    setBookingComplete(false);
    setError(null);
    setAssigningContact(false);
  }, [name, decodedName]);

  useEffect(() => {
    patientDataRef.current = patientData;
  }, [patientData]);

  const mergedUser = useMemo(
    () => ({
      calendars: userFromState?.calendars || [],
      primaryCalendar: userFromState?.primaryCalendar || null,
      hasCalendar: userFromState?.hasCalendar || false,
      userId: userFromState?.userId || userFromState?.id,
      ...clinicPractitioner,
      firstName:
        clinicPractitioner?.firstName || userFromState?.firstName || "",
      lastName: clinicPractitioner?.lastName || userFromState?.lastName || "",
      name: clinicPractitioner?.name || userFromState?.name || decodedName,
      email: clinicPractitioner?.email || userFromState?.email || "",
    }),
    [userFromState, clinicPractitioner, decodedName],
  );

  useEffect(() => {
    const handleBeforeUnload = async () => {
      if (
        !assignmentCompletedRef.current &&
        mergedUser?.userId &&
        patientData?.email
      ) {
        try {
          const assignmentData = {
            contactEmail: patientData.email,
            userId: mergedUser.userId,
          };
          const beaconSent = navigator.sendBeacon(
            `${BASE_URL}/user/assign-patient-to-practitioner`,
            JSON.stringify(assignmentData),
          );
          if (!beaconSent) {
            await fetch(`${BASE_URL}/user/assign-patient-to-practitioner`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(assignmentData),
              keepalive: true,
            });
          }
          assignmentCompletedRef.current = true;
        } catch (err) {
          console.error("Error assigning client on window close:", err);
        }
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [mergedUser?.userId, patientData?.email]);

  const practitionerData = useMemo(
    () => extractPractitionerData(mergedUser, decodedName),
    [mergedUser, decodedName],
  );

  const calendarId =
    practitionerData?.primaryCalendar?.id ||
    practitionerData?.calendars?.[0]?.id ||
    null;

  const assignPatientToPractitioner = async () => {
    try {
      setAssigningContact(true);
      setError(null);
      const contactEmail = patientDataRef.current?.email;
      const practitionerUserId = practitionerData?.userId;
      if (!contactEmail) throw new Error("No client email found");
      if (!practitionerUserId)
        throw new Error("Practitioner user ID not found");
      const response = await axios.post(
        `${BASE_URL}/user/assign-patient-to-practitioner`,
        { contactEmail, userId: practitionerUserId },
      );
      setAssigningContact(false);
      return response.data;
    } catch (err) {
      setAssigningContact(false);
      throw err;
    }
  };

  const handleOpenCalendarInNewTab = () => {
    if (!calendarId) {
      setError("No calendar ID found for this practitioner");
      return;
    }
    if (!patientData?.email) {
      setError("Please complete the registration form first");
      return;
    }
    if (!paymentVerifiedRef.current) {
      setShowPaymentModal(true);
      return;
    }
    const calendarUrl = buildGhlCalendarUrl(calendarId, patientData);
    const popupWindow = window.open(
      calendarUrl,
      "_blank",
      "width=1000,height=800",
    );
    setCalendarWindowRef(popupWindow);
    setError(null);
    if (popupWindow) {
      if (windowCheckIntervalRef.current)
        clearInterval(windowCheckIntervalRef.current);
      windowCheckIntervalRef.current = setInterval(() => {
        if (popupWindow.closed) {
          clearInterval(windowCheckIntervalRef.current);
          windowCheckIntervalRef.current = null;
          setCalendarWindowRef(null);
          if (
            !assignmentCompletedRef.current &&
            mergedUser?.userId &&
            patientData?.email
          ) {
            assignPatientToPractitioner()
              .then(() => {
                assignmentCompletedRef.current = true;
                setBookingComplete(true);
              })
              .catch(() =>
                setError("Assignment failed. Please contact support."),
              );
          }
        }
      }, 500);
    }
  };

  const handleBackNavigation = () => {
    dispatch(setQuestionnaireStepper(13));
    navigate("/");
  };

  const handlePaymentVerificationSuccess = (verificationData) => {
    setPaymentVerified(true);
    paymentVerifiedRef.current = true;
    setSavedCardInfo({
      last4: verificationData.last4,
      cardBrand: verificationData.cardBrand,
      expMonth: verificationData.expMonth,
      expYear: verificationData.expYear,
    });
    setShowPaymentModal(false);
  };

  const isLoading = reduxLoading || (!location.state?.user && staffLoading);

  return {
    isLoading,
    reduxError,
    userFromState,
    clinicPractitioner,
    practitionerData,
    patientData,
    assigningContact,
    bookingComplete,
    error,
    setError,
    calendarWindowRef,
    checkingPayment,
    paymentVerified,
    savedCardInfo,
    showPaymentModal,
    setShowPaymentModal,
    handleOpenCalendarInNewTab,
    handleBackNavigation,
    handlePaymentVerificationSuccess,
  };
}
